import { useTranslation } from 'react-i18next';
import { CHANNEL_ICONS, channelLabelKey, channelTone, isSellChannel } from '@/types/sell-channel';

interface ChannelBadgeProps {
  /**
   * Off the wire, so `unknown`-ish on purpose: an order payload that predates the column, or one a
   * surface never re-read, carries no channel at all and must render nothing rather than a chip
   * labelled `undefined`.
   */
  channel: string | null | undefined;
  /**
   * The chat this order came in from, when it was PLACED somewhere else — `originConversationId`.
   *
   * A web order the agent handed off mid-cart is `WEB` by design (D18: the surface that places the
   * sale owns the stamp) and reporting depends on it staying that way, so the acquisition side is
   * said in the label rather than by bending the channel. Only read for `WEB`: on a WhatsApp order
   * the origin and the placement are the same thread and the suffix would be noise.
   */
  originConversationId?: string | null;
  /** `sm` is the board card's pill; `md` suits a panel header, where it sits next to a title. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * The "where this order was placed" chip — WhatsApp, Web or the external agent.
 *
 * One component rather than the markup the board card used to own alone, because the detail panel
 * that reads it is reused on three surfaces (the board, the customer profile, the transfers page)
 * and the channel has to look the same on all of them. The label comes from `orders:board.channel.*`
 * — board-namespaced by history, already read from outside the board by the printed ticket.
 *
 * A web order that started in a chat reads "Web · desde WhatsApp": the chip keeps the WEB tint and
 * glyph because that is where the sale was made, and names the origin so an operator is not left
 * thinking a WhatsApp customer never wrote in.
 */
export function ChannelBadge({ channel, originConversationId, size = 'sm', className = '' }: ChannelBadgeProps) {
  const { t } = useTranslation('orders');
  if (!isSellChannel(channel)) return null;

  const fromChat = channel === 'WEB' && !!originConversationId;
  const Icon = CHANNEL_ICONS[channel];
  const metrics = size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]';
  const glyph = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${metrics} ${channelTone(channel)} ${className}`}
    >
      <Icon className={glyph} />
      {fromChat ? t('board.channel.webFromChat') : t(`board.channel.${channelLabelKey(channel)}`)}
    </span>
  );
}
